import React from "react";
import GoogleAvatar from "./GoogleAvatar";

interface EmailPreviewProps {
  htmlContent: string;
  subject: string;
  senderEmail: string;
  senderName?: string;
  senderPhoto?: string;
  recipientEmail: string;
  /** Whether to render body in iframe (for templates with full HTML structure) */
  isTemplate?: boolean;
  /** Optional template name to display as badge */
  templateName?: string;
  /** Max height for the body content area */
  bodyMaxHeight?: string;
  /** Optional className for the container */
  className?: string;
}

/**
 * Reusable email preview component that displays emails in a consistent
 * email client-like wrapper with sender info, recipient, subject, and body.
 *
 * Used in both the compose modal preview and email history view.
 */
const EmailPreview: React.FC<EmailPreviewProps> = ({
  htmlContent,
  subject,
  senderEmail,
  senderName,
  senderPhoto,
  recipientEmail,
  isTemplate = false,
  templateName,
  bodyMaxHeight = "400px",
  className = "",
}) => {
  return (
    <div className={`rounded-xl overflow-hidden shadow-lg bg-white ${className}`}>
      {/* Email Header */}
      <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <GoogleAvatar
            src={senderPhoto}
            name={senderName}
            email={senderEmail}
            size={40}
            className="rounded-full flex-shrink-0"
            fallbackClassName="rounded-full bg-navy-mid flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">
                {senderName ? `${senderName} ` : ""}
                <span className="text-gray-500 font-normal">
                  {senderName ? `<${senderEmail}>` : senderEmail}
                </span>
              </span>
            </div>
            <div className="text-sm text-gray-500 mt-0.5">To: {recipientEmail}</div>
          </div>
          {/* Template badge */}
          {templateName && (
            <span className="text-xs px-2 py-0.5 rounded bg-rose-dim text-rose-accent flex-shrink-0">
              {templateName}
            </span>
          )}
        </div>
        {/* Subject Line */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <h3 className="font-semibold text-gray-900 text-lg">{subject}</h3>
        </div>
      </div>

      {/* Email Body */}
      {isTemplate ? (
        <iframe
          srcDoc={htmlContent}
          className="w-full border-0 bg-white"
          style={{ height: bodyMaxHeight }}
          title="Email Preview"
        />
      ) : (
        <div
          className="px-5 py-4 bg-white overflow-y-auto"
          style={{ maxHeight: bodyMaxHeight }}
        >
          <div
            className="email-preview prose prose-sm max-w-none text-gray-900"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </div>
      )}
    </div>
  );
};

export default EmailPreview;
